import React from 'react';
import { graphql } from 'gatsby';

import { isProductionEnv } from '@/utils/envHelper';
import BlockWrapper from '@/components/BlockWrapper';
import Container from '@/components/Container';
import { usePageCustomizationContext } from '@/hooks/usePageCustomizationContext';

import SelligentContentRenderer from './SelligentContentRenderer';
import EngageContentRenderer from './EngageContentRenderer';

export type ReactComponentType = {
  name: string;
  component: string;
  locale: 'en' | 'fr' | 'nl';
};

type Props = ReactComponentType;

const OPPA_Component = (LoadedComponent: ReactComponentType, props) => {
  const { locale } = props;
  const env = isProductionEnv ? 'prod' : 'test';

  const pageCustomization = usePageCustomizationContext();

  return (
    <BlockWrapper padding='large'>
      <Container narrow={true}>
        <LoadedComponent
          lang={locale}
          env={env}
          customization={pageCustomization}
        />
      </Container>
    </BlockWrapper>
  );
};

const Selligent_Component = (props) => {
  const { data, locale } = props;

  return (
    <BlockWrapper padding='large'>
      <Container>
        <SelligentContentRenderer id={data} lang={locale} />
      </Container>
    </BlockWrapper>
  );
};

const Engage_Component = (props) => {
  const { data, locale } = props;

  return (
    <BlockWrapper padding='large'>
      <Container>
        <EngageContentRenderer id={data} lang={locale} />
      </Container>
    </BlockWrapper>
  );
};

const AVAILABLE_COMPONENTS = {
  OPPA_SoftkeyRecovery: (props) => {
    const SoftkeyRecovery = require('@keytrade/softkey-recovery-module');
    return OPPA_Component(SoftkeyRecovery, props);
  },
  OPPA_OnboardingRedirector: (props) => {
    const OnboardingRedirector = require('@keytrade/onboarding-redirector-module');
    return OPPA_Component(OnboardingRedirector, props);
  },
  OPPA_HardkeyActivation: (props) => {
    const HardkeyActivation = require('@keytrade/hardkey-activation-module');
    return OPPA_Component(HardkeyActivation, props);
  },
  OPPA_PersonalSpace: (props) => {
    const PersonalSpace = require('@keytrade/personal-space-module');
    return OPPA_Component(PersonalSpace, props);
  },
  Selligent_Content: (props) => Selligent_Component(props),

  Engage_Component: (props) => Engage_Component(props),
};

const ReactComponent: React.FC<Props> = (props) => {
  const { component } = props;

  if (typeof AVAILABLE_COMPONENTS[component] !== 'undefined') {
    return AVAILABLE_COMPONENTS[component](props);
  }

  return null;
};

export default ReactComponent;

export const externalContentQuery = graphql`
  fragment ReactComponentFragment on ContentfulReactComponent {
    contentful_id
    __typename
    name
    component
    data
  }
`;
